/**
 * AI CONTEXT: support-system - Support ticket and inquiry modal form
 * Journal: J-20250817-01
 * 
 * Purpose:
 * - Collect support tickets and general inquiries from users
 * - Auto-populate user information from authentication context
 * - Validate and sanitize input to prevent injection attacks
 * - Submit to appropriate backend endpoints
 * 
 * Architecture decisions:
 * - Use glassmorphism design consistent with app theme
 * - Implement client-side validation with server-side backup
 * - Auto-collect user data to reduce friction
 * - Separate support tickets from general inquiries
 * 
 * Future considerations:
 * - Add file attachment support
 * - Implement priority levels
 * - Add category selection
 * - Consider AI-powered suggestions
 */

import React, { useState, useEffect } from 'react';
import { X, Send, AlertCircle, CheckCircle, User, Mail, Phone, MapPin } from 'lucide-react';

import { isApiConfigured, authorizedFetch } from '../services/apiClient';
import toast from 'react-hot-toast';

const SupportModal = ({ user, onClose, onSubmit, isRateLimited }) => {
  const [formData, setFormData] = useState({
    type: 'support', // 'support' or 'general'
    subject: '',
    message: '',
    priority: 'medium' // 'low', 'medium', 'high'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [userProfile, setUserProfile] = useState(null);

  // Load user profile data on mount
  useEffect(() => {
    const loadUserProfile = async () => {
      if (user && isApiConfigured()) {
        try {
          const response = await authorizedFetch('/my/profile', { method: 'GET' });
          if (response && response.shippingAddress) {
            setUserProfile(response);
          }
        } catch (error) {
          console.warn('Could not load user profile:', error);
        }
      }
    };

    loadUserProfile();
  }, [user]);

  // Input validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.length > 100) {
      newErrors.subject = 'Subject must be 100 characters or less';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length > 2000) {
      newErrors.message = 'Message must be 2000 characters or less';
    }

    // Check for potential injection attacks
    const injectionPatterns = [
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi
    ];

    if (injectionPatterns.some(pattern => pattern.test(formData.subject) || pattern.test(formData.message))) {
      newErrors.general = 'Invalid characters detected in your input';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isRateLimited) {
      toast.error('Please wait before submitting another inquiry');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const endpoint = formData.type === 'support' ? '/support/tickets' : '/support/inquiries';
      
      const payload = {
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        priority: formData.priority,
        userEmail: user.email,
        userName: user.name || user.email,
        userPhone: userProfile?.phone || '',
        userAddress: userProfile?.shippingAddress || null,
        timestamp: new Date().toISOString()
      };

      if (isApiConfigured()) {
        const response = await authorizedFetch(endpoint, {
          method: 'POST',
          body: JSON.stringify(payload)
        });

        if (response && response.success) {
          toast.success('Your inquiry has been submitted successfully!');
          onSubmit();
        } else {
          throw new Error(response?.message || 'Failed to submit inquiry');
        }
      } else {
        // Fallback for development
        console.log('Support submission (dev mode):', payload);
        toast.success('Your inquiry has been submitted successfully! (Dev Mode)');
        onSubmit();
      }
    } catch (error) {
      console.error('Error submitting support inquiry:', error);
      toast.error(error.message || 'Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes with sanitization
  const handleInputChange = (field, value) => {
    // Basic sanitization
    let sanitizedValue = value;
    if (typeof value === 'string') {
      // Remove potentially dangerous characters
      sanitizedValue = value.replace(/[<>]/g, '').trim();
    }

    setFormData(prev => ({
      ...prev,
      [field]: sanitizedValue
    }));

    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Prevent scroll propagation for iOS modal containment
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="glass-card-dark rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden"
        onClick={handleModalClick}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-theme-silver/20">
          <div className="flex items-center space-x-3">
            <div className="bg-custom-orange/20 rounded-lg p-2">
              <AlertCircle className="h-6 w-6 text-custom-orange" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-theme-silver">Get Support</h2>
              <p className="text-sm text-theme-silver-light">We're here to help!</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-theme-silver hover:text-theme-bronze transition-colors duration-200"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Inquiry Type Selection */}
          <div>
            <label className="block text-sm font-medium text-theme-silver mb-3">
              What can we help you with?
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleInputChange('type', 'support')}
                className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                  formData.type === 'support'
                    ? 'border-custom-orange bg-custom-orange/20 text-custom-orange'
                    : 'border-theme-silver/20 text-theme-silver hover:border-theme-silver/40'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  <AlertCircle className="h-5 w-5" />
                  <span className="text-sm font-medium">Support</span>
                  <span className="text-xs opacity-75">Technical issues</span>
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => handleInputChange('type', 'general')}
                className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                  formData.type === 'general'
                    ? 'border-custom-orange bg-custom-orange/20 text-custom-orange'
                    : 'border-theme-silver/20 text-theme-silver hover:border-theme-silver/40'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  <CheckCircle className="h-5 w-5" />
                  <span className="text-sm font-medium">General</span>
                  <span className="text-xs opacity-75">Questions & info</span>
                </div>
              </button>
            </div>
          </div>

          {/* Priority Selection (for support tickets) */}
          {formData.type === 'support' && (
            <div>
              <label className="block text-sm font-medium text-theme-silver mb-2">
                Priority Level
              </label>
              <select
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                className="glass-input w-full"
              >
                <option value="low">Low - General question</option>
                <option value="medium">Medium - Minor issue</option>
                <option value="high">High - Urgent problem</option>
              </select>
            </div>
          )}

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-theme-silver mb-2">
              Subject *
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              className={`glass-input w-full ${errors.subject ? 'border-red-500' : ''}`}
              placeholder="Brief description of your issue or question"
              maxLength={100}
            />
            {errors.subject && (
              <p className="text-red-400 text-sm mt-1">{errors.subject}</p>
            )}
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-theme-silver mb-2">
              Message *
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              className={`glass-input w-full h-32 resize-none ${errors.message ? 'border-red-500' : ''}`}
              placeholder="Please provide detailed information about your issue or question..."
              maxLength={2000}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.message && (
                <p className="text-red-400 text-sm">{errors.message}</p>
              )}
              <span className="text-xs text-theme-silver-light ml-auto">
                {formData.message.length}/2000
              </span>
            </div>
          </div>

          {/* User Information Display */}
          <div className="bg-glass-white/5 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-medium text-theme-silver">Your Information</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-theme-silver-light" />
                <span className="text-theme-silver-light">Name:</span>
                <span className="text-theme-silver">{user.name || user.email}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-theme-silver-light" />
                <span className="text-theme-silver-light">Email:</span>
                <span className="text-theme-silver">{user.email}</span>
              </div>
              {userProfile?.phone && (
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-theme-silver-light" />
                  <span className="text-theme-silver-light">Phone:</span>
                  <span className="text-theme-silver">{userProfile.phone}</span>
                </div>
              )}
              {userProfile?.shippingAddress && (
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4 text-theme-silver-light" />
                  <span className="text-theme-silver-light">Address:</span>
                  <span className="text-theme-silver text-xs">
                    {userProfile.shippingAddress.line1}, {userProfile.shippingAddress.city}, {userProfile.shippingAddress.state}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* General Errors */}
          {errors.general && (
            <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-3">
              <p className="text-red-400 text-sm">{errors.general}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || isRateLimited}
            className={`
              w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2
              ${isSubmitting || isRateLimited
                ? 'bg-gray-500 cursor-not-allowed text-gray-300'
                : 'bg-custom-orange hover:bg-custom-orange/90 text-white transform hover:scale-105'
              }
            `}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span>Submit {formData.type === 'support' ? 'Support Ticket' : 'Inquiry'}</span>
              </>
            )}
          </button>

          {/* Rate Limit Notice */}
          {isRateLimited && (
            <p className="text-center text-sm text-theme-silver-light">
              ⏰ Please wait before submitting another inquiry
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default SupportModal;
